import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Star } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { Button } from '../../components/Shared/Button';
import { Badge } from '../../components/Shared/Badge';
import { EmptyState } from '../../components/Shared/EmptyState';
import { getPublicPlans } from '../../api/generated/public-plans/public-plans';
import {
  BusinessPlan,
  type PublicSubscriptionPlanDto,
} from '../../api/types/admin';
import { PublicShell } from './PublicShell';
import { formatMoney } from '../../utils/money';

const { getApiV1PublicSubscriptionPlans } = getPublicPlans();

function ctaKey(bp: number | undefined): 'starter' | 'professional' | 'enterprise' | 'default' {
  switch (bp) {
    case BusinessPlan.Starter:
      return 'starter';
    case BusinessPlan.Professional:
      return 'professional';
    case BusinessPlan.Enterprise:
      return 'enterprise';
    default:
      return 'default';
  }
}

export default function PricingPage() {
  const { t } = useTranslation(['public', 'common']);

  const [plans, setPlans] = useState<PublicSubscriptionPlanDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadPlans = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await getApiV1PublicSubscriptionPlans();
      const sorted = [...(response ?? [])].sort(
        (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
      );
      setPlans(sorted);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  return (
    <PublicShell>
      <div className="text-center max-w-2xl mx-auto mb-10">
        <h1 className="text-[36px] font-semibold text-slate-900 tracking-tight">
          {t('public:pricing.title')}
        </h1>
        <p className="text-[15px] text-slate-600 mt-2">{t('public:pricing.subtitle')}</p>
      </div>

      {loading ? (
        <p className="text-[13px] text-slate-500 text-center py-8">{t('public:pricing.loading')}</p>
      ) : error ? (
        <EmptyState variant="error" title={t('public:pricing.error')} onRetry={loadPlans} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {plans.map((plan) => {
            const isPopular = plan.businessPlan === BusinessPlan.Professional;
            const priceLabel =
              plan.price == null
                ? t('public:pricing.onRequest')
                : formatMoney(plan.price, { currency: plan.currency, wholeUnits: true });
            const employeesLabel =
              plan.maxEmployees == null
                ? t('public:pricing.unlimited')
                : t('public:pricing.maxEmployees', { count: plan.maxEmployees });

            return (
              <div
                key={`${plan.businessPlan}-${plan.name}`}
                className={`rounded-xl border bg-white flex flex-col relative ${
                  isPopular ? 'border-[#00A871]' : 'border-slate-200'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge variant="success" className="px-2.5 py-1">
                      <Star className="w-3 h-3 mr-1" strokeWidth={2} />
                      {t('public:pricing.mostPopular')}
                    </Badge>
                  </div>
                )}

                <div className="px-5 py-5 border-b border-slate-100">
                  <h3 className="text-[20px] font-semibold text-slate-900 tracking-tight">
                    {plan.name}
                  </h3>
                  {plan.description && (
                    <p className="text-[13px] text-slate-500 mt-1">{plan.description}</p>
                  )}
                </div>

                <div className="px-5 py-5 flex-1 space-y-4">
                  <div>
                    <div className="text-[36px] font-mono font-semibold tracking-tight text-slate-900 tabular-nums">
                      {priceLabel}
                    </div>
                    {plan.price != null && (
                      <div className="text-[13px] text-slate-500">
                        {t('public:pricing.perMonth')}
                      </div>
                    )}
                  </div>

                  <div className="text-[13px] text-slate-700 font-medium">{employeesLabel}</div>

                  {plan.features && plan.features.length > 0 && (
                    <ul className="space-y-2">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-[13px] text-slate-700">
                          <Check
                            className="w-3.5 h-3.5 mt-0.5 text-[#00A871] flex-shrink-0"
                            strokeWidth={3}
                          />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="px-5 py-4 border-t border-slate-100">
                  <Link to={`/kontakt?plan=${plan.businessPlan ?? 0}`} className="block">
                    <Button
                      variant={isPopular ? 'primary' : 'secondary'}
                      className="w-full justify-center"
                    >
                      {t(`public:pricing.cta.${ctaKey(plan.businessPlan)}`)}
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PublicShell>
  );
}
